import {
    Container,
    useMediaQuery,
    Table,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Typography,
    TableBody,
    TableHead,
    Tabs,
    Tab,
} from '@mui/material';
import Box from '@mui/material/Box';
import { useTheme, alpha } from '@mui/material/styles';
import { useState } from 'react';
import GradientHeading from 'components/GradientHeading';
import schedules from './scheduleRows';

const SymposiumSchedule = () => {
    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.up('md'), {
        defaultMatches: true,
    });

    const dateKeys = Object.keys(schedules);
    const [tabIndex, setTabIndex] = useState(0);
    const rows = schedules[dateKeys[tabIndex]] || [];
    const formatTitle = (t) => t ? t.charAt(0).toUpperCase() + t.slice(1) : '';

    const renderSession = (session, opts = {}) => {
        const presenters = Array.isArray(session?.presenter) ? session.presenter : [];
        const minH = typeof opts.minHeight === 'number'
            ? opts.minHeight
            : opts.header
            ? 140
            : 96;
        
        return (
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateRows: '1fr 1fr',
                    minHeight: { xs: minH, md: minH + 16 },
                    height: '100%',
                    gap: 0.5,
                }}
            >
                <Box>
                    {session?.session && (
                        <Box
                            sx={{
                                display: 'inline-block',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                                color: 'primary.main',
                                fontWeight: 800,
                                mb: 0.5,
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                {session.session}
                            </Typography>
                        </Box>
                    )}
                    <Typography variant="body1" fontWeight={600}>
                        {session?.title || '-'}
                    </Typography>
                </Box>
                <Box sx={{ alignSelf: 'stretch' }}>
                    {presenters.map((g, i) => (
                        <Typography key={i} variant="body2" color="text.secondary">
                            {formatTitle(g.title)}: {Array.isArray(g.name) ? g.name.join('; ') : ''}
                        </Typography>
                    ))}
                </Box>
            </Box>
        );
    };

    return (
        <Box
            sx={{
                py: { xs: 8, md: 12 },
                background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, white 60%, ${theme.palette.primary.main}08 100%)`,
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: -60,
                    right: -60,
                    width: 220,
                    height: 220,
                    borderRadius: '50%',
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
                    zIndex: 0,
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: -120,
                    left: -120,
                    width: 320,
                    height: 320,
                    borderRadius: '50%',
                    background: `linear-gradient(45deg, ${theme.palette.secondary.main}10, ${theme.palette.primary.main}10)`,
                    zIndex: 0,
                }}
            />
            <Container width={isMd ? '85vw' : '100vw'} my={2}>
                <Box py={6}>
                    <Box marginBottom={4}>
                        <GradientHeading label="Symposium Schedule" />
                    </Box>
                    <Tabs
                        value={tabIndex}
                        onChange={(_, v) => setTabIndex(v)}
                        sx={{ mb: 2 }}
                        centered
                    >
                        {dateKeys.map((d) => (
                            <Tab key={d} label={d} />
                        ))}
                    </Tabs>
                    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: `1px solid ${theme.palette.divider}` }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                                    <TableCell sx={{ width: { xs: 120, md: 160 }, fontWeight: 700 }}>
                                        Time
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>
                                        Pandanaran II
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, borderLeft: `1px solid ${theme.palette.divider}` }}>
                                        Pandanaran III
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row, idx) => {
                                    // Header row when time is not provided
                                    if (!row.time) {
                                        return (
                                            <TableRow
                                                key={`h-${idx}`}
                                                sx={{
                                                    background: `linear-gradient(90deg, ${theme.palette.primary.main}08 0%, ${theme.palette.secondary.main}08 100%)`,
                                                }}
                                            >
                                                <TableCell
                                                    sx={{
                                                        borderTop: `2px solid ${theme.palette.primary.main}22`,
                                                        fontWeight: 700,
                                                        background: `linear-gradient(90deg, ${theme.palette.primary.main}10 0%, transparent 100%)`,
                                                    }}
                                                >
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Session
                                                    </Typography>
                                                </TableCell>
                                                {['left', 'right'].map((side, sIdx) => (
                                                    <TableCell
                                                        key={side}
                                                        sx={{
                                                            borderTop: `2px solid ${theme.palette.primary.main}22`,
                                                            ...(sIdx === 1 ? { borderLeft: `1px solid ${theme.palette.divider}` } : {}),
                                                            ...(sIdx === 0
                                                                ? { background: `linear-gradient(90deg, ${theme.palette.primary.main}10 0%, transparent 100%)` }
                                                                : { background: `linear-gradient(90deg, ${theme.palette.secondary.main}10 0%, transparent 100%)` }),
                                                            verticalAlign: 'top',
                                                        }}
                                                    >
                                                        {renderSession(row[side], { header: true })}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        );
                                    }
                                    
                                    if (row.all) {
                                        return (
                                            <TableRow key={`a-${idx}`} sx={{ backgroundColor: `${theme.palette.info.main}08` }}>
                                                <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>
                                                    {row.time}
                                                </TableCell>
                                                <TableCell colSpan={2}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                        {renderSession(row.all, { minHeight: 88 })}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    }

                                    return (
                                        <TableRow
                                            key={`r-${idx}`}
                                            sx={{
                                                backgroundColor: idx % 2 === 0 ? 'grey.50' : 'background.paper',
                                            }}
                                        >
                                            <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>
                                                {row.time}
                                            </TableCell>
                                            <TableCell sx={{ verticalAlign: 'top' }}>{renderSession(row.left)}</TableCell>
                                            <TableCell sx={{ borderLeft: `1px solid ${theme.palette.divider}`, verticalAlign: 'top' }}>
                                                {row?.right ? renderSession(row.right) : ''}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Container>
        </Box>
    );
};

export default SymposiumSchedule;
